import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Check, Download, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CascadeFolderPicker } from '../components/CascadeFolderPicker';
import { TestBackLink, TestLoading, TestNotice, TestPageHeader, TestPanel, TestSelect } from '../components/TestAdminUi';
import { errorMessage } from '../lib/testAdminFormat';
import { testApi } from '../lib/testApi';

const importFields = ['QuesTitle', 'Opt1', 'Opt2', 'Opt3', 'Opt4', 'Opt5', 'CorrectAnswer', 'SolutionInfo', 'Type', 'Marks', 'NegativeMarks', 'CoursePtr', 'SubjectsPtr', 'ParentFolder'];
const sampleQuestion = {
  QuesTitle: 'What is the primary purpose of this sample question?',
  Opt1: 'Option A',
  Opt2: 'Option B',
  Opt3: 'Option C',
  Opt4: 'Option D',
  Opt5: '',
  CorrectAnswer: 'A',
  SolutionInfo: 'Add an explanation for the correct answer.',
  Type: 'MCQText',
  Marks: 4,
  NegativeMarks: 1,
  CoursePtr: '',
  SubjectsPtr: '',
  ParentFolder: '',
};
type ImportRow = Record<string, unknown>;
type Mapping = Record<string, string>;

export function TestAdminQuestionImport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Mapping>({});
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [courseId, setCourseId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [folderId, setFolderId] = useState('');
  const [courses, setCourses] = useState<Array<{ objectId: string; CourseTitle?: string }>>([]);
  const [subjects, setSubjects] = useState<Array<{ objectId: string; SubjectName?: string }>>([]);
  const [folders, setFolders] = useState<ImportRow[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  useEffect(() => {
    testApi.listCourses()
      .then((response) => setCourses(response.results))
      .catch((reason) => setError(errorMessage(reason, 'Courses could not be loaded.')))
      .finally(() => setCatalogLoading(false));
  }, []);

  useEffect(() => {
    setSubjectId('');
    setFolderId('');
    setSubjects([]);
    setFolders([]);
    if (!courseId) return;
    testApi.listSubjects(courseId)
      .then((response) => setSubjects(response.results))
      .catch((reason) => setError(errorMessage(reason, 'Subjects could not be loaded.')));
  }, [courseId]);

  useEffect(() => {
    setFolderId('');
    setFolders([]);
    if (!courseId || !subjectId) return;
    testApi.listQuestions({ type: 'Folder', courseId, subjectId, limit: 100 })
      .then((response) => setFolders(response.results))
      .catch((reason) => setError(errorMessage(reason, 'Question folders could not be loaded.')));
  }, [courseId, subjectId]);

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const nextRows = XLSX.utils.sheet_to_json<ImportRow>(firstSheet, { defval: '', raw: false }).slice(0, 500);
      if (!nextRows.length) throw new Error('The selected file has no data rows.');
      const nextColumns = Object.keys(nextRows[0]);
      const nextMapping = Object.fromEntries(nextColumns.map((column) => [column, importFields.includes(column) ? column : '']));
      setRows(nextRows);
      setColumns(nextColumns);
      setMapping(nextMapping);
      setFileName(file.name);
    } catch (reason) {
      setRows([]);
      setColumns([]);
      setMapping({});
      setError(errorMessage(reason, 'The question file could not be read.'));
    } finally {
      setLoading(false);
    }
  };

  const importRows = async () => {
    if (!courseId || !subjectId) {
      setError('Select a course and subject before importing.');
      return;
    }
    const mappedRows: Array<Record<string, unknown>> = rows.map((row) => ({
      ...Object.fromEntries(Object.entries(mapping).filter(([, target]) => target).map(([source, target]) => [target, row[source]])),
      CoursePtr: courseId,
      SubjectsPtr: subjectId,
      ...(folderId ? { ParentFolder: folderId } : {}),
    }));
    if (!mappedRows.some((row) => String(row.QuesTitle || '').trim())) { setError('Map a question title column before importing.'); return; }
    setSaving(true);
    setError('');
    try {
      const result = await testApi.importQuestions(mappedRows);
      setNotice(`${result.count} questions imported.`);
      setTimeout(() => navigate('/test-admin/questions'), 450);
    } catch (reason) {
      const details = reason && typeof reason === 'object' && 'rowErrors' in reason ? (reason as { rowErrors?: Array<{ row: number; messages: string[] }> }).rowErrors : undefined;
      setError(details?.length ? `${errorMessage(reason, 'The question import could not be completed.')} Rows: ${details.map((item) => `${item.row} (${item.messages.join(' ')})`).join('; ')}` : errorMessage(reason, 'The question import could not be completed.'));
    } finally {
      setSaving(false);
    }
  };

  const downloadSample = () => {
    const worksheet = XLSX.utils.json_to_sheet([sampleQuestion], { header: importFields });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions');
    XLSX.writeFile(workbook, 'ayurguide-question-import-sample.xlsx');
  };

  const folderOptions = folders.map((folder) => { const parent = folder.ParentFolder; const parentId = parent && typeof parent === 'object' && 'objectId' in parent ? String(parent.objectId || '') : typeof parent === 'string' ? parent.split('$').pop() : undefined; return { objectId: String(folder.objectId), title: String(folder.QuesTitle || 'Untitled folder'), parentId }; });
  return <div className="test-question-import-page"><TestBackLink to="/test-admin/questions">Back to question bank</TestBackLink><TestPageHeader kicker="AyurGuide / Question bank" title="Import questions" description="Preview a CSV or XLSX file, map its columns to the existing question schema, and import only validated rows."><button type="button" className="btn btn-soft" onClick={downloadSample}><Download size={16} />Download sample Excel</button><label className="btn btn-primary"><FileSpreadsheet size={16} />Choose CSV or XLSX<input className="test-hidden-file" type="file" accept=".csv,.xlsx,.xls" onChange={(event) => { void readFile(event.target.files?.[0]); event.currentTarget.value = ''; }} /></label></TestPageHeader>{notice && <TestNotice tone="success">{notice}</TestNotice>}{error && <TestNotice>{error}</TestNotice>}{loading ? <TestLoading label="Reading question file" /> : !rows.length ? <TestPanel className="test-import-empty"><FileSpreadsheet size={25} /><h2>Choose an import file</h2><p>Use the first row for column names. Supported fields include QuesTitle, Opt1–Opt5, CorrectAnswer, Type, Marks, NegativeMarks, CoursePtr, SubjectsPtr, and ParentFolder.</p>  </TestPanel> : <><TestPanel className="test-import-summary"><div><strong>{fileName}</strong><span>{rows.length} rows loaded · {columns.length} columns</span></div><button className="btn btn-ghost" onClick={() => { setRows([]); setColumns([]); setMapping({}); setFileName(''); }}>Clear</button></TestPanel><TestPanel className="test-import-mapping"><div className="test-panel-heading"><div><span className="test-kicker">Import destination</span><h2>Choose where to place these questions</h2></div><span className="test-form-lock">AyurGuide catalog</span></div><div className="test-form-grid test-form-grid-tight"><TestSelect label="Course" value={courseId} onChange={setCourseId} options={courses.map((course) => ({ value: course.objectId, label: course.CourseTitle || 'Untitled course' }))} required /><TestSelect label="Subject" value={subjectId} onChange={setSubjectId} options={subjects.map((subject) => ({ value: subject.objectId, label: subject.SubjectName || 'Untitled subject' }))} required /><CascadeFolderPicker label="Question folder" value={folderId} onChange={setFolderId} folders={folderOptions} /></div>{catalogLoading && <span className="test-form-lock">Loading AyurGuide courses…</span>}<p className="test-import-destination-note">The selected course and subject are applied to every imported row. Folder is optional.</p></TestPanel><TestPanel className="test-import-mapping"><div className="test-panel-heading"><div><span className="test-kicker">Column mapping</span><h2>Match file columns</h2></div><span className="test-form-lock">No Common Data fields</span></div><div className="test-import-mapping-grid">{columns.map((column) => <label className="test-field" key={column}><span>{column}</span><select value={mapping[column] || ''} onChange={(event) => setMapping((current) => ({ ...current, [column]: event.target.value }))}><option value="">Skip column</option>{importFields.map((field) => <option value={field} key={field}>{field}</option>)}</select></label>)}</div></TestPanel><TestPanel className="test-import-preview"><div className="test-panel-heading"><div><span className="test-kicker">Preview</span><h2>First five rows</h2></div><span>{rows.length > 5 ? `+${rows.length - 5} more` : ''}</span></div><div className="test-import-preview-table"><div className="test-import-preview-row header">{columns.slice(0, 6).map((column) => <span key={column}>{column}</span>)}</div>{rows.slice(0, 5).map((row, index) => <div className="test-import-preview-row" key={index}>{columns.slice(0, 6).map((column) => <span key={column}>{String(row[column] || '').slice(0, 80)}</span>)}</div>)}</div></TestPanel><div className="test-editor-actions"><button className="btn btn-ghost" onClick={() => navigate('/test-admin/questions')}>Cancel</button><button className="btn btn-primary" disabled={saving} onClick={() => void importRows()}>{saving ? <><RefreshCw size={16} />Importing…</> : <><Check size={16} />Validate and import</>}</button></div></>}</div>;
}
